import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardStats as DashboardStatsType, adminApiService } from '../../services/adminApiService';
import { useAdminApiService } from '../../hooks/useAdminApiService';
import IGamingSummary from './IGamingSummary';
import P2PGamesSummary from './P2PGamesSummary';
import KucoinBalances from './KucoinBalances';
import ActiveTradingBots from './ActiveTradingBots';
import './AdminComponents.css';

interface StatCard {
  title: string;
  value: string;
  icon: string;
  trend?: {
    value: string;
    isPositive: boolean;
  };
  color: string;
  permission?: string; // Add permission field to each card
}

// Cache duration in milliseconds (5 minutes)
const CACHE_DURATION = 5 * 60 * 1000;

// Helper functions for cache management
const getCachedData = (key: string) => {
  try {
    const cached = sessionStorage.getItem(key);
    if (cached) {
      const { data, timestamp } = JSON.parse(cached);
      const now = Date.now();
      if (now - timestamp < CACHE_DURATION) {
        console.log(`DashboardStats: Using cached data for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        return data;
      } else {
        console.log(`DashboardStats: Cache expired for ${key}, age: ${Math.round((now - timestamp) / 1000)}s`);
        sessionStorage.removeItem(key);
      }
    }
  } catch (error) {
    console.error(`Failed to get cached data for ${key}:`, error);
  }
  return null;
};

const setCachedData = (key: string, data: any) => {
  try {
    sessionStorage.setItem(key, JSON.stringify({
      data,
      timestamp: Date.now()
    }));
    console.log(`DashboardStats: Cached data for ${key}`);
  } catch (error) {
    console.error(`Failed to cache data for ${key}:`, error);
  }
};

const DashboardStats: React.FC = () => {
  const [stats, setStats] = useState<DashboardStatsType | null>(() => getCachedData('dashboard-stats'));
  const [portfolioStats, setPortfolioStats] = useState<{
    totalCryptoValue: number;
    totalAssetValue: number;
    totalPortfolioValue: number;
    cryptoGrowth: number;
    assetGrowth: number;
  } | null>(() => getCachedData('portfolio-stats'));
  const [kycSummary, setKycSummary] = useState<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    inReview: number;
    today: number;
  } | null>(null);
  const [kybSummary, setKybSummary] = useState<{
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    inReview: number;
    today: number;
  } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);
  const [isLoadingPortfolio, setIsLoadingPortfolio] = useState(false);
  const [adminPermissions, setAdminPermissions] = useState<string[]>(() => getCachedData('admin-permissions') || []);

  const adminApi = useAdminApiService();
  const navigate = useNavigate();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const isInitializedRef = useRef(false);
  const renderCountRef = useRef(0);
  const isLoadingStatsRef = useRef(false);
  const isLoadingPortfolioRef = useRef(false);
  const canViewSpotCoinsSection = adminPermissions.includes('VIEW_ADMIN_TRADING_SPOT_COINS_SECTION');

  // Track render count for debugging
  renderCountRef.current += 1;
  console.log('DashboardStats component rendering:', { 
    stats, 
    loading, 
    error, 
    renderCount: renderCountRef.current,
    adminApiStable: adminApi ? 'stable' : 'unstable'
  });

  // Component lifecycle debugging
  useEffect(() => {
    console.log('DashboardStats component mounted, render count:', renderCountRef.current);
    return () => {
      console.log('DashboardStats component unmounted, render count:', renderCountRef.current);
      // Reset initialization flag on unmount so component can reinitialize when remounted
      isInitializedRef.current = false;
      // Clean up interval on unmount
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []);

  // Memoize the load functions to prevent recreation on every render
  const loadDashboardStats = useCallback(async (isRefresh = false) => {
    console.log('Loading dashboard stats:', { isRefresh });
    
    // Check cache first (unless it's a manual refresh)
    if (!isRefresh) {
      const cachedStats = getCachedData('dashboard-stats');
      if (cachedStats) {
        console.log('DashboardStats: Using cached dashboard stats, skipping API call');
        setStats(cachedStats);
        
        // Extract KYC/KYB summary from cached data
        if (cachedStats.kycSummary) {
          setKycSummary(cachedStats.kycSummary);
        }
        if (cachedStats.kybSummary) {
          setKybSummary(cachedStats.kybSummary);
        }
        
        setLoading(false);
        return;
      }
    }
    
    // Prevent concurrent requests using ref (not state to avoid dependency loop)
    if (isLoadingStatsRef.current) {
      console.log('DashboardStats: Already loading stats, skipping...');
      return;
    }
    
    isLoadingStatsRef.current = true;
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    
    setIsLoadingStats(true);
    
    try {
      const response = await adminApi.getDashboardStats();
      console.log('Dashboard stats response:', response);
      if (response.success && response.data) {
        setStats(response.data);
        setCachedData('dashboard-stats', response.data);
        
        // Extract KYC/KYB summary from the consolidated response
        const data = response.data as any; // Temporary type assertion
        if (data.kycSummary) {
          setKycSummary(data.kycSummary);
          console.log('KYC summary extracted from dashboard stats:', data.kycSummary);
        }
        
        if (data.kybSummary) {
          setKybSummary(data.kybSummary);
          console.log('KYB summary extracted from dashboard stats:', data.kybSummary);
        }
        
        setError('');
        console.log('Dashboard stats loaded successfully:', response.data);
      } else {
        const errorMsg = response.error || 'Failed to load dashboard stats';
        setError(errorMsg);
        console.error('Dashboard stats error:', errorMsg);
      }
    } catch (error: any) {
      const errorMsg = error.message || 'Failed to load dashboard stats';
      setError(errorMsg);
      console.error('Dashboard stats exception:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
      setIsLoadingStats(false);
      isLoadingStatsRef.current = false;
    }
  }, [adminApi]); // Only depend on adminApi, not isLoadingStats

  const loadPortfolioStats = useCallback(async (isRefresh = false) => {
    console.log('Loading portfolio stats');
    
    // Check cache first (unless it's a manual refresh)
    if (!isRefresh) {
      const cachedPortfolio = getCachedData('portfolio-stats');
      if (cachedPortfolio) {
        console.log('DashboardStats: Using cached portfolio stats, skipping API call');
        setPortfolioStats(cachedPortfolio);
        return;
      }
    }
    
    // Prevent concurrent requests using ref (not state to avoid dependency loop)
    if (isLoadingPortfolioRef.current) {
      console.log('DashboardStats: Already loading portfolio, skipping...');
      return;
    }
    
    isLoadingPortfolioRef.current = true;
    setIsLoadingPortfolio(true);
    
    try {
      const response = await adminApi.getPortfolioStats();
      console.log('Portfolio stats response:', response);
      if (response.success && response.data) {
        setPortfolioStats(response.data);
        setCachedData('portfolio-stats', response.data);
        console.log('Portfolio stats loaded successfully:', response.data);
      }
    } catch (error: any) {
      console.error('Portfolio stats exception:', error);
    } finally {
      setIsLoadingPortfolio(false);
      isLoadingPortfolioRef.current = false;
    }
  }, [adminApi]); // Only depend on adminApi, not isLoadingPortfolio

  // KYC/KYB summaries are now loaded as part of the consolidated dashboard stats

  const loadAdminPermissions = useCallback(async (isRefresh = false) => {
    // Check cache first (unless it's a manual refresh)
    if (!isRefresh) {
      const cachedPermissions = getCachedData('admin-permissions');
      if (cachedPermissions) {
        console.log('DashboardStats: Using cached admin permissions, skipping API call');
        setAdminPermissions(cachedPermissions);
        return;
      }
    }
    
    try {
      const response = await adminApiService.getAdminProfile();
      console.log('Admin profile response:', response);
      if (response.success && response.data && response.data.permissions) {
        setAdminPermissions(response.data.permissions);
        setCachedData('admin-permissions', response.data.permissions);
        console.log('Admin permissions loaded:', response.data.permissions);
        console.log('User role:', response.data.role);
        
        // KYC/KYB summaries are now loaded as part of the consolidated dashboard stats
        console.log('DashboardStats: Profile loaded, KYC/KYB summaries will be included in dashboard stats');
      } else {
        console.error('Failed to load admin profile:', response.error);
      }
    } catch (error) {
      console.error('Failed to load admin permissions:', error);
    }
  }, []);

  useEffect(() => {
    console.log('DashboardStats useEffect triggered, isInitialized:', isInitializedRef.current);
    // Only initialize once per mount
    if (isInitializedRef.current) {
      console.log('DashboardStats: Already initialized (per mount), skipping...');
      return;
    }

    console.log('DashboardStats useEffect triggered - initializing');
    isInitializedRef.current = true;

    console.log('DashboardStats: Calling load functions...');
    loadDashboardStats();
    loadPortfolioStats();
    loadAdminPermissions(); // KYC/KYB summaries are now included in dashboard stats

    // Auto-refresh every 2 minutes to reduce API calls
    intervalRef.current = setInterval(() => {
      console.log('DashboardStats: Auto-refreshing data...');
      loadDashboardStats(true); // Force refresh, skip cache
      loadPortfolioStats(true); // Force refresh, skip cache
      loadAdminPermissions(true); // Force refresh, skip cache
    }, 120000);

    console.log('DashboardStats: Auto-refresh interval set for 2 minutes');

    return () => {
      console.log('DashboardStats: Clearing auto-refresh interval');
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // Removed loadDashboardStats and loadPortfolioStats from dependencies since they're now stable

  const handleRefresh = useCallback(() => {
    console.log('DashboardStats: Manual refresh triggered');
    loadDashboardStats(true);
    loadPortfolioStats(true);
    loadAdminPermissions(true);
    // KYC/KYB summaries are now included in dashboard stats
  }, [loadDashboardStats, loadPortfolioStats, loadAdminPermissions]);

  const formatNumber = useCallback((num: number): string => {
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(1)}M`;
    } else if (num >= 1000) {
      return `${(num / 1000).toFixed(1)}K`;
    }
    return num.toString();
  }, []);

  const getStatCards = useMemo((): StatCard[] => {
    if (!stats) return [];

    const baseCards = [
      {
        title: 'Total Customers',
        value: formatNumber(stats.totalCustomers),
        icon: '👥',
        trend: {
          value: '+12.5%',
          isPositive: true
        },
        color: 'blue',
        permission: 'VIEW_DASHBOARD_TOTAL_CUSTOMERS'
      },
      {
        title: 'Active Customers',
        value: formatNumber(stats.activeCustomers),
        icon: '✅',
        trend: {
          value: '+8.2%',
          isPositive: true
        },
        color: 'green',
        permission: 'VIEW_DASHBOARD_ACTIVE_CUSTOMERS'
      },
      {
        title: 'Total Accounts',
        value: formatNumber(stats.totalAccounts),
        icon: '💳',
        trend: {
          value: '+15.7%',
          isPositive: true
        },
        color: 'purple',
        permission: 'VIEW_DASHBOARD_TOTAL_ACCOUNTS'
      },
      {
        title: 'Total Crypto Portfolio',
        value: portfolioStats ? `$${(portfolioStats.totalCryptoValue / 1000000).toFixed(1)}M` : '$0',
        icon: '₿',
        trend: portfolioStats ? {
          value: `+${portfolioStats.cryptoGrowth.toFixed(1)}%`,
          isPositive: portfolioStats.cryptoGrowth >= 0
        } : undefined,
        color: 'orange',
        permission: 'VIEW_DASHBOARD_CRYPTO_PORTFOLIO'
      },
      {
        title: 'Total Asset Portfolio',
        value: portfolioStats ? `$${(portfolioStats.totalAssetValue / 1000000).toFixed(1)}M` : '$0',
        icon: '📈',
        trend: portfolioStats ? {
          value: `+${portfolioStats.assetGrowth.toFixed(1)}%`,
          isPositive: portfolioStats.assetGrowth >= 0
        } : undefined,
        color: 'green',
        permission: 'VIEW_DASHBOARD_ASSET_PORTFOLIO'
      },
      {
        title: 'Compliance Alerts: Basic Registration',
        value: formatNumber(stats.pendingAlerts),
        icon: '⚠️',
        trend: {
          value: stats.pendingAlerts > 10 ? 'High' : 'Normal',
          isPositive: stats.pendingAlerts <= 5
        },
        color: stats.pendingAlerts > 10 ? 'red' : 'orange',
        permission: 'VIEW_DASHBOARD_COMPLIANCE_ALERTS'
      },
      {
        title: 'KYT Analysis',
        value: 'Open',
        icon: '🔍',
        color: 'orange',
        permission: 'VIEW_KYT'
      },
      {
        title: 'Trading Bots',
        value: 'Manage',
        icon: '🤖',
        color: 'blue',
        permission: 'VIEW_DASHBOARD_TRADING_BOTS'
      },
      {
        title: 'Pending Transactions',
        value: formatNumber(stats.pendingTransactions),
        icon: '⏳',
        trend: {
          value: stats.pendingTransactions > 50 ? 'High' : 'Normal',
          isPositive: stats.pendingTransactions <= 20
        },
        color: stats.pendingTransactions > 50 ? 'red' : 'yellow',
        permission: 'VIEW_DASHBOARD_PENDING_TRANSACTIONS'
      },
      {
        title: 'System Health',
        value: 'Excellent',
        icon: '🛡️',
        trend: {
          value: '99.9%',
          isPositive: true
        },
        color: 'green',
        permission: 'VIEW_DASHBOARD_SYSTEM_HEALTH'
      },
      {
        title: 'Enhanced KYC Review',
        value: (() => {
          console.log('DashboardStats: Rendering KYC card, kycSummary:', kycSummary);
          return kycSummary ? formatNumber(kycSummary.total) : '0';
        })(),
        icon: '📋',
        trend: kycSummary ? {
          value: kycSummary.pending > 0 ? `${kycSummary.pending} pending` : 'All clear',
          isPositive: kycSummary.pending === 0
        } : undefined,
        color: kycSummary && kycSummary.pending > 5 ? 'red' : kycSummary && kycSummary.pending > 0 ? 'orange' : 'green',
        permission: 'VIEW_DASHBOARD_KYC_REVIEWS'
      },
      {
        title: 'Enhanced KYB Review',
        value: (() => {
          console.log('DashboardStats: Rendering KYB card, kybSummary:', kybSummary);
          return kybSummary ? formatNumber(kybSummary.total) : '0';
        })(),
        icon: '🏢',
        trend: kybSummary ? {
          value: kybSummary.pending > 0 ? `${kybSummary.pending} pending` : 'All clear',
          isPositive: kybSummary.pending === 0
        } : undefined,
        color: kybSummary && kybSummary.pending > 5 ? 'red' : kybSummary && kybSummary.pending > 0 ? 'orange' : 'green',
        permission: 'VIEW_DASHBOARD_KYB_REVIEWS'
      }
    ];

    // Filter cards based on user permissions
    console.log('Filtering cards with permissions:', { adminPermissions, baseCards: baseCards.map(c => ({ title: c.title, permission: c.permission })) });
    return baseCards.filter(card => {
      const hasPermission = !card.permission || adminPermissions.includes(card.permission);
      console.log(`Card ${card.title} permission check:`, { permission: card.permission, hasPermission, adminPermissions });
      return hasPermission;
    });
  }, [stats, portfolioStats, formatNumber, adminPermissions, kycSummary, kybSummary]);

  if (loading && !refreshing) {
    return (
      <div className="dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading dashboard statistics...</p>
      </div>
    );
  }

  if (error && !stats) {
    return (
      <div className="dashboard-error">
        <div className="error-icon">❌</div>
        <h3>Failed to Load Dashboard</h3>
        <p>{error}</p>
        <button className="retry-btn" onClick={() => loadDashboardStats()}>
          Try Again
        </button>
      </div>
    );
  }

  // Remove the function call since getStatCards is now a memoized value
  const statCards = getStatCards;

  return (
    <div className="dashboard-stats">
      <div className="dashboard-header">
        <div className="dashboard-title">
          <h2>System Overview</h2>
          <p>Real-time banking system statistics and metrics</p>
        </div>
        <div className="dashboard-actions">
          <button 
            className={`refresh-btn ${refreshing ? 'refreshing' : ''}`}
            onClick={handleRefresh}
            disabled={refreshing}
          >
            🔄 {refreshing ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>
      </div>

      <div className="stats-grid">
        {statCards.map((card: StatCard, index: number) => (
          <div
            key={index}
            className={`stat-card ${card.color}`}
            onClick={() => {
              if (card.title === 'Compliance Alerts: Basic Registration') {
                try { window.sessionStorage.setItem('complianceActiveSubTab', 'alerts'); } catch {}
                navigate('/admin/dashboard?tab=compliance');
                // let Compliance decide default tab (Basic Registration)
              } else if (card.title === 'Enhanced KYC Review') {
                try { window.sessionStorage.setItem('complianceActiveSubTab', 'kyc'); } catch {}
                navigate('/admin/dashboard?tab=compliance');
              } else if (card.title === 'Enhanced KYB Review') {
                try { window.sessionStorage.setItem('complianceActiveSubTab', 'kyb'); } catch {}
                navigate('/admin/dashboard?tab=compliance');
            } else if (card.title === 'KYT Analysis') {
              navigate('/admin/dashboard?tab=kyt');
              } else if (card.title === 'Trading Bots') {
                navigate('/admin/dashboard?tab=tradingBots');
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="stat-header">
              <div className="stat-icon">{card.icon}</div>
              <div className="stat-title">{card.title}</div>
            </div>
            <div className="stat-content">
              <div className="stat-value">{card.value}</div>
              {card.trend && (
                <div className={`stat-trend ${card.trend.isPositive ? 'positive' : 'negative'}`}>
                  <span className="trend-icon">
                    {card.trend.isPositive ? '📈' : '📉'}
                  </span>
                  <span className="trend-value">{card.trend.value}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* iGaming and P2P Games Summary Cards */}
      <div className="summary-cards-section">
        <IGamingSummary />
        <P2PGamesSummary />
      </div>

      {/* KuCoin spot coins section */}
      {(adminPermissions.includes('VIEW_ADMIN_TRADING_BOTS') || 
        adminPermissions.includes('MANAGE_TRADING_BOTS')) && (
        <>
          {canViewSpotCoinsSection && (
            <div className="kucoin-balances-section" style={{ marginBottom: '24px' }}>
              <KucoinBalances />
            </div>
          )}
          <div className="active-trading-bots-section">
            <ActiveTradingBots />
          </div>
        </>
      )}

      <div className="dashboard-insights">
        {adminPermissions.includes('VIEW_DASHBOARD_QUICK_INSIGHTS') && (
          <div className="insight-card">
            <h3>🎯 Quick Insights</h3>
          <div className="insights-list">
            <div className="insight-item">
              <span className="insight-bullet">•</span>
              <span>Customer growth rate increased by 12.5% this month</span>
            </div>
            <div className="insight-item">
              <span className="insight-bullet">•</span>
              <span>
                {portfolioStats ? (
                  portfolioStats.cryptoGrowth > 10 
                    ? `Crypto portfolio showing strong growth at +${portfolioStats.cryptoGrowth.toFixed(1)}%`
                    : `Crypto portfolio growth: +${portfolioStats.cryptoGrowth.toFixed(1)}% - stable performance`
                ) : 'Loading crypto portfolio data...'}
              </span>
            </div>
            <div className="insight-item">
              <span className="insight-bullet">•</span>
              <span>
                {portfolioStats ? (
                  portfolioStats.assetGrowth > 5 
                    ? `Traditional assets performing well with +${portfolioStats.assetGrowth.toFixed(1)}% growth`
                    : `Asset portfolio growth: +${portfolioStats.assetGrowth.toFixed(1)}% - steady returns`
                ) : 'Loading asset portfolio data...'}
              </span>
            </div>
            <div className="insight-item">
              <span className="insight-bullet">•</span>
              <span>
                {stats?.pendingAlerts ? (
                  stats.pendingAlerts > 10 
                    ? `${stats.pendingAlerts} alerts require immediate attention`
                    : `${stats.pendingAlerts} pending alerts - manageable level`
                ) : 'No pending alerts - system running smoothly'}
              </span>
            </div>
            <div className="insight-item">
              <span className="insight-bullet">•</span>
              <span>
                {stats?.pendingTransactions ? (
                  stats.pendingTransactions > 50
                    ? `${stats.pendingTransactions} transactions pending approval - review recommended`
                    : `${stats.pendingTransactions} transactions pending - normal processing`
                ) : 'All transactions processed'}
              </span>
            </div>
            <div className="insight-item">
              <span className="insight-bullet">•</span>
              <span>System uptime: 99.9% - Excellent performance</span>
            </div>
          </div>
        </div>
        )}

        {adminPermissions.includes('VIEW_DASHBOARD_SYSTEM_STATUS') && (
          <div className="insight-card">
            <h3>📊 System Status</h3>
          <div className="status-indicators">
            <div className="status-item">
              <div className="status-dot green"></div>
              <span>Core Banking Services</span>
              <span className="status-value">Operational</span>
            </div>
            <div className="status-item">
              <div className="status-dot green"></div>
              <span>Payment Processing</span>
              <span className="status-value">Operational</span>
            </div>
            <div className="status-item">
              <div className="status-dot green"></div>
              <span>Crypto Trading</span>
              <span className="status-value">Operational</span>
            </div>
            <div className="status-item">
              <div className="status-dot green"></div>
              <span>Asset Management</span>
              <span className="status-value">Operational</span>
            </div>
            <div className="status-item">
              <div className="status-dot yellow"></div>
              <span>Compliance Monitoring</span>
              <span className="status-value">Under Review</span>
            </div>
            <div className="status-item">
              <div className="status-dot green"></div>
              <span>Customer Portal</span>
              <span className="status-value">Operational</span>
            </div>
          </div>
        </div>
        )}
      </div>

      <div className="dashboard-footer">
        <div className="last-updated">
          Last updated: {new Date().toLocaleString()}
        </div>
        <div className="auto-refresh">
          <span className="refresh-indicator">🔄</span>
          Auto-refreshes every 2 minutes
        </div>
      </div>
    </div>
  );
};

export default DashboardStats; 